import type { MenuTreeNodeVO } from "@/api/system/menus/type";

/** 勾选状态：全选 / 半选 / 未选 */
export type PermissionCheckState = "checked" | "indeterminate" | "unchecked";

/**
 * 收集菜单树全部节点 ID（含端展开时批量写入 expandedMenus）
 * @param nodes 后端菜单树节点列表
 */
export const collectMenuTreeNodeIds = (nodes: MenuTreeNodeVO[]): string[] => {
  const ids: string[] = [];
  const walk = (list: MenuTreeNodeVO[]) => {
    list.forEach((node) => {
      ids.push(node.id);
      if (node.children?.length) walk(node.children);
    });
  };
  walk(nodes);
  return ids;
};

/** 获取页面及其子节点全部可分配菜单 id */
export const getPagePermissionIds = (page: MenuTreeNodeVO): string[] => {
  return collectMenuTreeNodeIds([page]);
};

/** 获取菜单及其子节点全部可分配菜单 id */
export const getMenuPermissionIds = (menu: MenuTreeNodeVO): string[] => {
  return collectMenuTreeNodeIds([menu]);
};

/** 获取节点下全部子孙菜单 id（不含自身） */
export const getDescendantPermissionIds = (node: MenuTreeNodeVO): string[] => {
  if (!node.children?.length) return [];
  return collectMenuTreeNodeIds(node.children);
};

/** 根据已选 id 计算任意节点勾选状态（子级全选时父级视为全选） */
export const getNodeCheckState = (
  node: MenuTreeNodeVO,
  selectedIds: string[],
): PermissionCheckState => {
  const descendantIds = getDescendantPermissionIds(node);
  if (descendantIds.length === 0) {
    return selectedIds.includes(node.id) ? "checked" : "unchecked";
  }
  const selectedCount = descendantIds.filter((id) => selectedIds.includes(id)).length;
  if (selectedCount === 0) return "unchecked";
  if (selectedCount === descendantIds.length) return "checked";
  return "indeterminate";
};

/** 根据已选 id 计算菜单勾选状态 */
export const getMenuCheckState = (
  menu: MenuTreeNodeVO,
  selectedIds: string[],
): PermissionCheckState => getNodeCheckState(menu, selectedIds);

/** 根据已选 id 计算页面勾选状态 */
export const getPageCheckState = (
  page: MenuTreeNodeVO,
  selectedIds: string[],
): PermissionCheckState => getNodeCheckState(page, selectedIds);

/**
 * 子级全部选中时自动补齐父级 id，部分选中或未选时移除父级 id
 * @param selectedIds 当前已选菜单 id
 * @param nodes 菜单树根节点列表
 */
export const syncParentPermissionIds = (
  selectedIds: string[],
  nodes: MenuTreeNodeVO[],
): string[] => {
  const nextSet = new Set(selectedIds);
  /** 后序遍历：先处理子节点再判断父节点是否应选中 */
  const walk = (node: MenuTreeNodeVO) => {
    node.children?.forEach(walk);
    const descendantIds = getDescendantPermissionIds(node);
    if (!descendantIds.length) return;
    const allSelected = descendantIds.every((id) => nextSet.has(id));
    const someSelected = descendantIds.some((id) => nextSet.has(id));
    if (allSelected) {
      nextSet.add(node.id);
    } else if (!someSelected) {
      nextSet.delete(node.id);
    } else {
      nextSet.delete(node.id);
    }
  };
  nodes.forEach(walk);
  return [...nextSet];
};

/** 按端分组的已选权限 */
export interface PermissionSelectionByApp {
  /** 系统端 ID */
  systemTypeId: string;
  /** 该端已选菜单 ID 列表 */
  menuIds: string[];
}

/**
 * 将扁平已选 id 按端分组
 * @param selectedIds 已选菜单 id 列表
 * @param groups 可选菜单分组
 */
export const groupSelectedIdsByApp = (
  selectedIds: string[],
  groups: { systemTypeId: string; menuTree?: MenuTreeNodeVO[] }[],
): PermissionSelectionByApp[] => {
  const selectedSet = new Set(selectedIds);
  return groups.map((group) => {
    const appMenuIds = collectMenuTreeNodeIds(group.menuTree ?? []);
    const menuIds = appMenuIds.filter((id) => selectedSet.has(id));
    return { systemTypeId: group.systemTypeId, menuIds };
  });
};

/** 判断 value 是否为按端分组结构 */
export const isPermissionSelectionByApp = (
  value: string[] | PermissionSelectionByApp[],
): value is PermissionSelectionByApp[] =>
  value.length > 0 &&
  typeof value[0] === "object" &&
  value[0] !== null &&
  "systemTypeId" in value[0];

/**
 * 将受控 value 统一为扁平 menuId 列表（兼容 Form 按端分组与详情扁平数组）
 * @param value 已选权限：扁平 id 或按端分组
 */
export const flattenPermissionSelection = (
  value?: string[] | PermissionSelectionByApp[],
): string[] => {
  if (!value?.length) return [];
  if (isPermissionSelectionByApp(value)) {
    return value.flatMap((item) => item.menuIds);
  }
  return value;
};

/** 子节点展示分组：PAGE 独占一行，连续 BUTTON 合并为同一行 */
export type MenuChildRenderGroup =
  | { type: "page"; node: MenuTreeNodeVO }
  | { type: "buttons"; nodes: MenuTreeNodeVO[] };

/** 将菜单子节点按 UI 布局分组，保持原始顺序 */
export const groupMenuChildrenForLayout = (children: MenuTreeNodeVO[]): MenuChildRenderGroup[] => {
  const groups: MenuChildRenderGroup[] = [];
  let buttonBuffer: MenuTreeNodeVO[] = [];
  /** 刷出缓冲中的按钮组 */
  const flushButtons = () => {
    if (buttonBuffer.length > 0) {
      groups.push({ type: "buttons", nodes: buttonBuffer });
      buttonBuffer = [];
    }
  };
  children.forEach((child) => {
    if (child.menuLevel === "PAGE") {
      flushButtons();
      groups.push({ type: "page", node: child });
    } else {
      buttonBuffer.push(child);
    }
  });
  flushButtons();
  return groups;
};
